import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Users,
  Calculator,
  Clock,
  Receipt,
  FlaskConical,
  Settings,
  LogOut,
  ShieldCheck,
  CreditCard,
  Briefcase,
  FileCheck,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { Logo } from "../Logo";
import UserBadge from "./UserBadge";

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/invoices", label: "Fatture", icon: FileText, dataTour: "nav-invoices" },
  { path: "/preventivi", label: "Preventivi", icon: FileCheck },
  { path: "/clients", label: "Clienti", icon: Users },
  { path: "/fiscal", label: "Fiscale", icon: Calculator, dataTour: "nav-fiscal" },
  { path: "/timesheet", label: "Timesheet", icon: Clock },
  { path: "/f24", label: "F24", icon: Receipt },
  { path: "/commercialista", label: "Commercialista", icon: Briefcase },
  { path: "/simulazioni", label: "Simulazioni", icon: FlaskConical, dataTour: "nav-simulazioni" },
  { path: "/abbonamento", label: "Abbonamento", icon: CreditCard },
];

export default function Sidebar({ darkMode }) {
  const { signOut } = useAuth();
  const { isAdmin } = useIsAdmin();
  const navItems = isAdmin
    ? [...NAV_ITEMS, { path: "/admin", label: "Admin", icon: ShieldCheck }]
    : NAV_ITEMS;

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <aside
      className={`hidden md:flex md:flex-col md:w-56 md:fixed md:inset-y-0 z-20 border-r transition-colors ${
        darkMode
          ? "bg-neutral-900 border-neutral-800"
          : "bg-white border-neutral-200"
      }`}
    >
      {/* Logo */}
      <div className={`p-5 border-b ${darkMode ? "border-neutral-800" : "border-neutral-200"}`}>
        <Logo variant="full" size="sm" darkMode={darkMode} className="cursor-pointer" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navItems.map(({ path, label, icon: Icon, dataTour }) => (
          <NavLink
            key={path}
            to={path}
            end={path === "/dashboard"}
            data-tour={dataTour}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? darkMode
                    ? "bg-forfy-500/15 text-forfy-300"
                    : "bg-forfy-50 text-forfy-700"
                  : darkMode
                    ? "text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
                    : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
              }`
            }
          >
            <Icon className="w-[18px] h-[18px] shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className={`p-3 border-t ${darkMode ? "border-neutral-800" : "border-neutral-200"}`}>
        <div className="flex items-center justify-between px-3 py-1.5">
          <span className={`text-xs font-medium ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
            Piano
          </span>
          <UserBadge />
        </div>

        <NavLink
          to="/settings"
          data-tour="nav-settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? darkMode
                  ? "bg-forfy-500/15 text-forfy-300"
                  : "bg-forfy-50 text-forfy-700"
                : darkMode
                  ? "text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
                  : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
            }`
          }
        >
          <Settings className="w-[18px] h-[18px] shrink-0" />
          Impostazioni
        </NavLink>

        <button
          onClick={handleSignOut}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mt-0.5 ${
            darkMode
              ? "text-red-400 hover:bg-neutral-800"
              : "text-red-600 hover:bg-red-50"
          }`}
        >
          <LogOut className="w-[18px] h-[18px] shrink-0" />
          Esci
        </button>
      </div>
    </aside>
  );
}
